// utils/validation.js or plugins/validation.js

import { defineRule, configure } from "vee-validate";
import {
  required,
  email,
  min,
  max,
  numeric,
  confirmed,
  min_value,
  regex,
} from "@vee-validate/rules";

// Register built-in rules
defineRule("required", required);
defineRule("email", email);
defineRule("min", min);
defineRule("max", max);
defineRule("numeric", numeric);
defineRule("confirmed", confirmed);
defineRule("min_value", min_value);
defineRule("regex", regex);

defineRule("numeric", (value) => {
  if (!value) return true;
  const numericRegex = /^\+?\d+$/;
  return numericRegex.test(value) || "This field must contain only numbers";
});

// URL validation
defineRule("url", (value) => {
  if (!value) return true;
  const urlRegex = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
  return urlRegex.test(value) || "This value must be a valid URL";
});

// Valid date check (must be a real date)
defineRule("date", (value) => {
  if (!value) return true;
  const parsedDate = new Date(value);
  return (
    (parsedDate instanceof Date && !isNaN(parsedDate)) || "The date is invalid"
  );
});

// Must be today or in the past
defineRule("past_date", (value) => {
  if (!value) return true;
  const parsedDate = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (
    (parsedDate instanceof Date && !isNaN(parsedDate) && parsedDate <= today) ||
    "The date must be today or in the past"
  );
});

// Must be today or in the future
defineRule("future_date", (value) => {
  if (!value) return true;
  const parsedDate = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (
    (parsedDate instanceof Date && !isNaN(parsedDate) && parsedDate >= today) ||
    "The date must be today or in the future"
  );
});

// Phone number (optional +, digits only, max 15)
defineRule("phone", (value) => {
  if (!value) return true;
  const phoneRegex = /^\+?[1-9]\d{0,15}$/;
  return phoneRegex.test(value) || "Please enter a valid phone number";
});

// Alphanumeric only
defineRule("alphanumeric", (value) => {
  if (!value) return true;
  const alphanumericRegex = /^[a-zA-Z0-9]+$/;
  return (
    alphanumericRegex.test(value) ||
    "This field must contain only letters and numbers"
  );
});

// Waybill format: e.g. AB12345678
defineRule("waybill_format", (value) => {
  if (!value) return true;
  const waybillRegex = /^[A-Z]{2}\d{8}$/;
  return (
    waybillRegex.test(value) ||
    "Waybill must be in format: XX12345678 (2 letters followed by 8 digits)"
  );
});

// Numeric with decimal
defineRule("numeric_decimal", (value) => {
  if (!value) return true; // skip empty (use "required" if needed)

  const decimalRegex = /^-?\d+(\.\d+)?$/;

  return decimalRegex.test(value) || "The field must be a valid number";
});

// Global Error Messages
function prettifyFieldName(field) {
  if (!field) return "";
  if (field.endsWith("_id")) {
    field = field.replace(/_id$/, "");
  }
  field = field.replace(/[._]\d+$/, "");
  let name = field.replace(/_/g, " ");
  name = name.replace(/([a-z])([A-Z])/g, "$1 $2");
  name = name.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));

  return name.trim();
}

configure({
  generateMessage: (ctx) => {
    const fieldName = prettifyFieldName(ctx.field);

    const messages = {
      required: `The ${fieldName} field is required`,
      email: `The ${fieldName} field must be a valid email`,
      min: `The ${fieldName} field must have at least ${ctx.rule.params[0]} characters`,
      max: `The ${fieldName} field must have no more than ${ctx.rule.params[0]} characters`,
      numeric: `The ${fieldName} field must be numeric`,
      confirmed: `The ${fieldName} field confirmation does not match`,
      min_value: `The ${fieldName} field must be at least ${ctx.rule.params[0]}`,
      regex: `The ${fieldName} field format is invalid`,
      url: `The ${fieldName} field must be a valid URL`,
      date: `The ${fieldName} field must be a valid date`,
      past_date: `The ${fieldName} field must be today or in the past`,
      future_date: `The ${fieldName} field must be today or in the future`,
      phone: `The ${fieldName} field must be a valid phone number`,
      alphanumeric: `The ${fieldName} field must contain only letters and numbers`,
      waybill_format: `The ${fieldName} field must be in correct waybill format`,
      numeric_decimal: `The ${fieldName} field must be a valid number with optional decimal`,
    };

    return messages[ctx.rule.name] || `The ${fieldName} field is invalid`;
  },
});
